import { computed, signal } from '@angular/core';

import { addDays, addMonths, addWeeks, startOfDay } from 'date-fns';

import { eachDay, snapToMinutes } from './helpers/date';
import { CamEventRecord } from './models/event-record';
import { CamRecord } from './models/record';
import { CamStore } from './models/store';
import { CamDateRange, CamViewPresetConfig } from './types';

export type CamSchedulerEventName = 'dateRangeChange' | 'modeChange' | 'refresh';

export type CamWorkingTime = {
  fromHour: number;
  toHour: number;
};

export type CamTimelineTick = {
  end: Date;
  start: Date;
};

const DEFAULT_TICK_WIDTH = 60;

export class CamTimeAxis {
  public readonly ticks: CamTimelineTick[];
  public readonly tickWidth: number;
  public readonly totalWidth: number;
  public readonly startDate: Date;
  public readonly endDate: Date;

  constructor(startDate: Date, endDate: Date, preset: CamViewPresetConfig, workingTime: CamWorkingTime | null) {
    this.tickWidth = preset.tickWidth ?? DEFAULT_TICK_WIDTH;
    this.ticks = this._buildTicks(startDate, endDate, preset, workingTime);
    this.totalWidth = this.ticks.length * this.tickWidth;
    this.startDate = this.ticks[0]?.start ?? startDate;
    this.endDate = this.ticks[this.ticks.length - 1]?.end ?? endDate;
  }

  public getXFromDate(date: Date): number {
    const time = date.getTime();
    if (!this.ticks.length || time <= this.ticks[0].start.getTime()) {
      return 0;
    }
    if (time >= this.endDate.getTime()) {
      return this.totalWidth;
    }
    const index = this._firstTickEndingAfter(time);
    if (index === -1) {
      return this.totalWidth;
    }
    const tick = this.ticks[index];
    if (time <= tick.start.getTime()) {
      return index * this.tickWidth;
    }
    const ratio = (time - tick.start.getTime()) / (tick.end.getTime() - tick.start.getTime());
    return (index + ratio) * this.tickWidth;
  }

  public getDateFromX(x: number): Date {
    if (!this.ticks.length) {
      return new Date();
    }
    const clamped = Math.max(0, Math.min(x, this.totalWidth - 1));
    const index = Math.min(this.ticks.length - 1, Math.floor(clamped / this.tickWidth));
    const tick = this.ticks[index];
    const ratio = (clamped - index * this.tickWidth) / this.tickWidth;
    return new Date(tick.start.getTime() + ratio * (tick.end.getTime() - tick.start.getTime()));
  }

  private _firstTickEndingAfter(time: number): number {
    let low = 0;
    let high = this.ticks.length;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (this.ticks[middle].end.getTime() > time) {
        high = middle;
      } else {
        low = middle + 1;
      }
    }
    return low === this.ticks.length ? -1 : low;
  }

  private _buildTicks(
    startDate: Date,
    endDate: Date,
    preset: CamViewPresetConfig,
    workingTime: CamWorkingTime | null
  ): CamTimelineTick[] {
    const days = eachDay(startDate, endDate);

    if (preset.base !== 'hourAndDay') {
      return days.map(day => ({ end: addDays(day, 1), start: day }));
    }

    const fromHour = workingTime?.fromHour ?? 0;
    const toHour = workingTime?.toHour ?? 24;

    return days.flatMap(day =>
      Array.from({ length: Math.max(1, toHour - fromHour) }, (_, index) => {
        const start = new Date(day);
        start.setHours(fromHour + index, 0, 0, 0);
        const end = new Date(start);
        end.setHours(start.getHours() + 1);
        return { end, start };
      })
    );
  }
}

export class CamSchedulerEngine {
  public readonly eventStore = new CamStore<CamEventRecord>();
  public readonly resourceStore = new CamStore<CamRecord>();
  public readonly resourceTimeRangeStore = new CamStore<CamRecord>();

  public readonly span = signal<CamDateRange>({
    endDate: addDays(startOfDay(new Date()), 1),
    startDate: startOfDay(new Date()),
  });
  public readonly preset = signal<CamViewPresetConfig>({ base: 'hourAndDay', tickWidth: DEFAULT_TICK_WIDTH });
  public readonly workingTime = signal<CamWorkingTime | null>(null);
  public readonly revision = signal(0);

  public startDate: Date;
  public endDate: Date;
  public visibleDateRange: CamDateRange;
  public viewPreset: CamViewPresetConfig;

  public timelineElement: HTMLElement | null = null;

  private readonly _axis = computed(() => {
    const span = this.span();
    return new CamTimeAxis(span.startDate, span.endDate, this.preset(), this.workingTime());
  });

  private _listeners = new Map<CamSchedulerEventName, Set<(...args: any[]) => void>>();
  private _destroyed = false;

  constructor() {
    this.visibleDateRange = this.span();
    this.startDate = this.visibleDateRange.startDate;
    this.endDate = this.visibleDateRange.endDate;
    this.viewPreset = this.preset();

    this.eventStore.changes$.subscribe({ next: () => this.revision.update(value => value + 1) });
    this.resourceStore.changes$.subscribe({ next: () => this.revision.update(value => value + 1) });
    this.resourceTimeRangeStore.changes$.subscribe({ next: () => this.revision.update(value => value + 1) });
  }

  public isDestroyed(): boolean {
    return this._destroyed;
  }

  public setViewPreset(preset: CamViewPresetConfig): void {
    this.preset.set(preset);
    this.viewPreset = preset;
    this.trigger('modeChange');
  }

  public getAxis(): CamTimeAxis {
    return this._axis();
  }

  public setStartDate(startDate: Date): void {
    const span = this.span();
    const duration = span.endDate.getTime() - span.startDate.getTime();
    this.setTimeSpan(startOfDay(startDate), new Date(startOfDay(startDate).getTime() + duration));
  }

  public setTimeSpan(startDate: Date, endDate: Date): void {
    this.span.set({ endDate: startOfDay(endDate), startDate: startOfDay(startDate) });
    this.visibleDateRange = this.span();
    this.startDate = this.visibleDateRange.startDate;
    this.endDate = this.visibleDateRange.endDate;
    this.trigger('dateRangeChange');
  }

  public shiftNext(amount: number = 1): void {
    this._shift(amount);
  }

  public shiftPrevious(amount: number = 1): void {
    this._shift(-amount);
  }

  public getDateFromXY(coordinates: [number, number], roundingMethod: string = 'round', local: boolean = true): Date {
    const rect = this.timelineElement?.getBoundingClientRect();
    const x = local || !rect ? coordinates[0] : coordinates[0] - rect.left;
    const date = this.getAxis().getDateFromX(x);

    if (roundingMethod !== 'round') {
      return date;
    }
    return snapToMinutes(date, this.preset().timeResolution?.increment ?? 15);
  }

  public resolveResourceRecord(event: { clientX?: number; clientY?: number; target?: EventTarget | null }): CamRecord {
    const target = event.target instanceof Element ? (event.target as HTMLElement) : null;
    const fromTarget = target?.closest<HTMLElement>('[data-resource-id]');
    const fromPoint =
      fromTarget ??
      (event.clientX !== undefined && event.clientY !== undefined
        ? (document
            .elementsFromPoint(event.clientX, event.clientY)
            .map(element => (element as HTMLElement).closest<HTMLElement>('[data-resource-id]'))
            .find(Boolean) ?? null)
        : null);

    const id = fromPoint?.dataset['resourceId'];
    return this.resourceStore.records.find(record => String(record.id) === id) ?? new CamRecord({ id });
  }

  public refresh(): void {
    this.revision.update(value => value + 1);
    this.trigger('refresh');
  }

  public on(eventName: CamSchedulerEventName, handler: (...args: any[]) => void): () => void {
    const handlers = this._listeners.get(eventName) ?? new Set();
    handlers.add(handler);
    this._listeners.set(eventName, handlers);
    return () => handlers.delete(handler);
  }

  public trigger(eventName: CamSchedulerEventName, payload?: any): void {
    this._listeners.get(eventName)?.forEach(handler => handler(payload));
  }

  public destroy(): void {
    this._destroyed = true;
    this._listeners.clear();
    this.timelineElement = null;
    this.eventStore.destroy();
    this.resourceStore.destroy();
    this.resourceTimeRangeStore.destroy();
  }

  private _shift(amount: number): void {
    const span = this.span();
    const duration = span.endDate.getTime() - span.startDate.getTime();
    const start = this._shiftDate(span.startDate, amount);
    this.setTimeSpan(start, new Date(start.getTime() + duration));
  }

  private _shiftDate(date: Date, amount: number): Date {
    switch (this.preset().base) {
      case 'dayAndWeek':
        return addWeeks(date, amount);
      case 'weekAndDayLetter':
        return addMonths(date, amount);
      default:
        return addDays(date, amount);
    }
  }
}
